import { useEffect, useRef, useState } from 'react';

interface ViewState {
  scale: number;
  tx: number;
  ty: number;
}

const DEFAULT: ViewState = { scale: 1, tx: 0, ty: 0 };
const MIN_SCALE = 0.5;
const MAX_SCALE = 4;

/**
 * Minimal zoom + pan handler for a container element. Returns a viewState
 * and bind handlers to spread on the container. Wheel = zoom, drag = pan.
 */
// Pointer needs to move at least this many pixels before a press
// converts from "potential click" to "active drag". Below the
// threshold, no capture happens and the click event reaches the
// landmark / region / route handlers underneath.
const DRAG_THRESHOLD_PX = 4;

export function useZoomPan() {
  const [view, setView] = useState<ViewState>(DEFAULT);
  // `pending` holds the candidate drag state from pointerdown. It
  // does NOT capture the pointer until movement exceeds the threshold.
  // `active` is true once we've actually entered drag mode.
  const pending = useRef<{
    startX: number;
    startY: number;
    startTx: number;
    startTy: number;
    pointerId: number;
    captured: boolean;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  function reset() {
    setView(DEFAULT);
  }

  function setScale(next: number, focusX?: number, focusY?: number) {
    setView((v) => {
      const clamped = Math.max(MIN_SCALE, Math.min(MAX_SCALE, next));
      if (focusX != null && focusY != null) {
        // Zoom around focus point: adjust tx,ty so that the focus point stays put
        const ratio = clamped / v.scale;
        const nx = focusX - (focusX - v.tx) * ratio;
        const ny = focusY - (focusY - v.ty) * ratio;
        return { scale: clamped, tx: nx, ty: ny };
      }
      return { ...v, scale: clamped };
    });
  }

  function flyTo(targetScale: number, focusXInContent: number, focusYInContent: number) {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    // Center the target content point in the container
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const tx = cx - focusXInContent * targetScale;
    const ty = cy - focusYInContent * targetScale;
    setView({ scale: targetScale, tx, ty });
  }

  function onWheel(e: React.WheelEvent<HTMLDivElement>) {
    if (!containerRef.current) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const focusX = e.clientX - rect.left;
    const focusY = e.clientY - rect.top;
    const delta = -e.deltaY * 0.0015;
    setScale(view.scale * (1 + delta), focusX, focusY);
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    // DO NOT setPointerCapture yet — that would redirect pointerup to
    // the viewport and break click events on inner SVG targets
    // (landmarks, regions, routes). Capture happens lazily inside
    // onPointerMove once we've moved past the drag threshold.
    pending.current = {
      startX: e.clientX,
      startY: e.clientY,
      startTx: view.tx,
      startTy: view.ty,
      pointerId: e.pointerId,
      captured: false,
    };
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const p = pending.current;
    if (!p) return;
    const dx = e.clientX - p.startX;
    const dy = e.clientY - p.startY;
    if (!p.captured) {
      // Still in the "is this a drag or a click" decision zone.
      if (dx * dx + dy * dy < DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) return;
      // Movement exceeded threshold — promote to active drag and
      // capture the pointer so we keep getting move events even when
      // the cursor leaves the viewport.
      try {
        e.currentTarget.setPointerCapture(p.pointerId);
        p.captured = true;
      } catch {
        /* ignore */
      }
    }
    setView((v) => ({ ...v, tx: p.startTx + dx, ty: p.startTy + dy }));
  }

  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    const p = pending.current;
    if (!p) return;
    if (p.captured) {
      try {
        e.currentTarget.releasePointerCapture(p.pointerId);
      } catch {
        /* ignore */
      }
    }
    pending.current = null;
  }

  // Wheel handler must use non-passive listener to call preventDefault
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function handle(ev: WheelEvent) {
      if (!containerRef.current) return;
      ev.preventDefault();
      const rect = containerRef.current.getBoundingClientRect();
      const focusX = ev.clientX - rect.left;
      const focusY = ev.clientY - rect.top;
      const delta = -ev.deltaY * 0.0015;
      setView((v) => {
        const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, v.scale * (1 + delta)));
        const ratio = next / v.scale;
        return {
          scale: next,
          tx: focusX - (focusX - v.tx) * ratio,
          ty: focusY - (focusY - v.ty) * ratio,
        };
      });
    }
    el.addEventListener('wheel', handle, { passive: false });
    return () => el.removeEventListener('wheel', handle);
  }, []);

  return {
    view,
    containerRef,
    bind: { onPointerDown, onPointerMove, onPointerUp, onWheel },
    reset,
    zoomIn: () => setScale(view.scale * 1.4),
    zoomOut: () => setScale(view.scale / 1.4),
    flyTo,
  };
}
